/**
 * useAuth Hook
 * Provides easy access to authentication context and methods
 */

import { useAuth as useAuthContext } from '../contexts/AuthContext';

/**
 * Custom hook to access authentication context
 * @returns {Object} Authentication context value
 */
export const useAuth = () => {
    return useAuthContext();
};

/**
 * Hook to check if user has specific permission
 * @param {string} permission - The permission to check
 * @returns {boolean} Whether the user has the permission
 */
export const usePermission = (permission) => {
    const { hasPermission } = useAuthContext();
    return hasPermission(permission);
};

/**
 * Hook to check if user has specific role
 * @param {string} role - The role to check
 * @returns {boolean} Whether the user has the role
 */
export const useRole = (role) => {
    const { user } = useAuthContext();
    
    if (!user) return false;
    
    // Check if user has the specific role
    if (user.role === role) return true;
    
    // Check if user is in a group with the role
    if (user.groups && Array.isArray(user.groups)) {
        return user.groups.some(group =>
            group.toLowerCase().includes(role.toLowerCase())
        );
    }
    
    return false;
};

/**
 * Hook to get current user information
 * @returns {Object|null} Current user object or null if not authenticated
 */
export const useCurrentUser = () => {
    const { user } = useAuthContext();
    return user;
};

/**
 * Hook to check authentication status
 * @returns {Object} Object with authentication status flags
 */
export const useAuthStatus = () => {
    const { isAuthenticated, loading, error } = useAuthContext();
    
    return {
        isAuthenticated,
        isLoading: loading,
        isError: !!error,
        error
    };
};

/**
 * Hook to get authentication methods
 * @returns {Object} Object with authentication methods
 */
export const useAuthActions = () => {
    const {
        login,
        logout,
        register,
        refreshSession,
        checkAuthState
    } = useAuthContext();
    
    return {
        login,
        logout,
        register,
        refreshSession,
        checkAuthState
    };
};

export default useAuth;